<script>
import { getCentralHeaders } from 'dashboard/helpers/centralApiHeaders';
import LoadingState from '../../../../components/widgets/LoadingState.vue';
import { mapActions, mapState } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default {
  name: "DeepCrawl",
  components: {
    LoadingState,
  },
  data() {
    return {
      url: null,
      isLoading: false,
      title: null,
      error: null,
      testData: '',
      links: '',
      crawlSubPages: false,
      maxDepth: 1,
      maxPages: 20,
      includePatterns: [],
      excludePatterns: [],
      isExtractingUrl: false,
    };
  },
  computed: {
    ...mapState('knowledge', ['crawlResult']),
    rawCrawlResult() {
      return this.crawlResult.data;
    },
    hasResult() {
      return !!(this.crawlResult && this.crawlResult.data && this.crawlResult.data.length);
    },
    proxyBase() {
      const accountId = this.$store.getters.getCurrentAccountId;
      return `/api/v1/accounts/${accountId}/proxy/deepcrawl`;
    },
    includePathsInput: {
      get() {
        return this.includePatterns.join(', ');
      },
      set(val) {
        this.includePatterns = val
          .split(',')
          .map(v => v.trim())
          .filter(Boolean);
      },
    },
    excludePathsInput: {
      get() {
        return this.excludePatterns.join(', ');
      },
      set(val) {
        this.excludePatterns = val
          .split(',')
          .map(v => v.trim())
          .filter(Boolean);
      },
    },
  },
  watch: {
    rawCrawlResult: {
      handler() {
        this.emitScrollDown();
      },
      deep: true,
    },
    isLoading(newVal) {
      this.$emit('isCrawling', newVal);
    }
  },
  methods: {
    ...mapActions('knowledge', ['handleRemoveFile']),
    emitScrollDown() {
      this.$emit('scrollDown');
    },
    async getLinks() {
      this.isExtractingUrl = true;
      const payload = {
        url: this.url,
        extractedLinks: true,
        metadata: true,
        folderFirst: true,
        linksOrder: "page",

        linkExtractionOptions: {
          includeExternal: false,
          includeMedia: false,
          removeQueryParams: true,
        },
      };

      const response = await fetch(`${this.proxyBase}/links`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getCentralHeaders(),
        },
        body: JSON.stringify(payload),
      });
      this.isExtractingUrl = false;
      if (!response.ok) {
        throw new Error(`Deepcrawl error: ${response.status}`);
      }

      return response.json();
    },
    flattenTree(tree, depth = 0, acc = []) {
      if (!tree || depth > this.maxDepth) return acc;

      acc.push(tree.url);

      if (!tree.children) return acc;

      for (const child of tree.children) {
        if (acc.length >= this.maxPages) break;
        this.flattenTree(child, depth + 1, acc);
      }

      return acc;
    },

    applyIncludeExclude(urls) {
      return urls.filter(url => {
        if (this.excludePatterns.some(p => url.includes(p))) return false;
        if (this.includePatterns.length) {
          return this.includePatterns.some(p => url.includes(p));
        }
        return true;
      });
    },
    async startDeepCrawl() {
      if (!this.url) {
        useAlert("Please enter a valid URL");
        return;
      }

      this.handleRemoveFile();
      this.isLoading = true;
      this.error = null;

      try {
        let urlsToCrawl = [this.url];

        // ✅ Step 1: fetch links if enabled
        if (this.crawlSubPages) {
          const linkResult = await this.getLinks();

          const flatUrls = this.flattenTree(linkResult.tree);
          urlsToCrawl = this.applyIncludeExclude(flatUrls).slice(0, this.maxPages);
        }

        // ✅ Step 2: initialize crawlResult with empty data array
        const crawledData = [];
        this.$store.commit('knowledge/setCrawlResult', { data: crawledData });

        // ✅ Step 3: crawl pages top → bottom and update state live
        for (const url of urlsToCrawl) {
          const res = await fetch(
            `${this.proxyBase}/read?url=${encodeURIComponent(url)}`,
            {
              headers: {
                Accept: "text/markdown",
                ...getCentralHeaders(),
              },
            }
          );

          if (!res.ok) continue;

          const markdown = await res.text();

          const titleMatch =
            markdown.match(/^#\s+(.+)$/m) ||
            markdown.match(/^##\s+(.+)$/m);

          const crawledPage = {
            metadata: {
              title: titleMatch ? titleMatch[1] : url,
              sourceUrl: url,
            },
            markdown,
          };

          // Add the crawled page to the data array and commit immediately for live updates
          crawledData.push(crawledPage);
          this.$store.commit('knowledge/setCrawlResult', { data: [...crawledData] });
        }

        // ✅ Step 4: mark upload as complete
        this.$store.commit('knowledge/setIsUploaded', true);

      } catch (err) {
        console.error(err);
        this.error = err.message;
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script>

<template>
  <div>
    <div class="my-4 p-6 rounded-md bg-slate-200 dark:bg-slate-800">
      <!-- URL + Action -->
      <span class="flex gap-2">
        <input
          type="text"
          v-model="url"
          placeholder="Enter URL to crawl"
          class="flex-1 p-2 border rounded"
          @change="isCrawled = false"
        />
        <woot-button
          @click="startDeepCrawl"
          :disabled="isLoading || !url"
          v-if="!isLoading"
        >
          Start Crawl
        </woot-button>
        <woot-button v-else class="flex items-center justify-center">
          <svg
            class="animate-spin h-5 w-5 text-gray-500"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <circle
              class="opacity-75"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
              stroke-dasharray="80"
              stroke-dashoffset="60"
            />
          </svg>
          <span>Loading</span>
        </woot-button>
      </span>

      <div v-if="isExtractingUrl" class="flex items-center gap-2 mt-3">
        <span class="spinner" v-if="isLoading" />
        <h1>
          <span class="font-bold text-woot-500" :class="{ 'animate-pulse': isLoading }">
            Extracting URLs...
          </span>
        </h1>
      </div>


      <!-- Crawl Result Summary -->
      <div v-if="hasResult" class="flex items-center gap-2 mt-3">
        <span class="spinner" v-if="isLoading" />
        <h1>
          <span class="font-bold text-woot-500" :class="{ 'animate-pulse': isLoading }">
            {{ rawCrawlResult.length || 0 }}
          </span>
          pages extracted
        </h1>
        <div @click="$emit('scrollDown')">
          <fluent-icon
            icon="chevron-down"
            class="cursor-pointer rounded text-white bg-woot-500 hover:bg-woot-700"
          />
        </div>
      </div>

      <hr class="my-4" />

      <!-- Options -->
      <h1 class="font-semibold mb-2">Crawl Options</h1>

      <label class="flex gap-2 items-center mb-3">
        <input type="checkbox" v-model="crawlSubPages" />
        <span>Crawl sub-pages</span>
      </label>

      <div v-if="crawlSubPages" class="grid grid-cols-2 gap-3 mb-3">
        <label class="flex flex-col text-sm">
          <span>Page limit</span>
          <input
            type="number"
            min="1"
            class="p-1 border rounded"
            v-model.number="maxPages"
          />
        </label>

        <label class="flex flex-col text-sm">
          <span>Maximum depth</span>
          <input
            type="number"
            min="1"
            class="p-1 border rounded"
            v-model.number="maxDepth"
          />
        </label>

        <label class="flex flex-col text-sm">
          <span>Exclude paths</span>
          <input
            type="text"
            class="p-1 border rounded"
            placeholder="/login, /pricing"
            v-model="excludePathsInput"
          />
          <small class="text-gray-500">
            Pages containing these paths will be skipped
          </small>
        </label>

        <label class="flex flex-col text-sm">
          <span>Include paths</span>
          <input
            type="text"
            class="p-1 border rounded"
            placeholder="/blog, /docs"
            v-model="includePathsInput"
          />
          <small class="text-gray-500">
            Only pages containing these paths will be crawled
          </small>
        </label>
      </div>

      <!-- Error -->
      <div v-if="error" class="text-red-500 mt-2">
        {{ error }}
      </div>

      <!-- Loading -->
      <LoadingState v-if="isLoading" class="mt-4" message="Crawling..." />
    </div>
  </div>
</template>